import React from 'react';
import { useTranslation } from 'react-i18next';
import Calendar from '@ingka/ssr-icon/paths/calendar-reschedule';
import { styled } from 'styled-components';
import colors from '../styles/colors';
import Button from '@ingka/button';
import Text from '@ingka/text';
import locationPin from '@ingka/ssr-icon/paths/location-pin';
import InlineMessage from '@ingka/inline-message';
import { getStoreName } from '../helpers/appHelper';
import { useStateSelector } from '../store/';
import { type ReplacementService } from '../services/appointment/model';

interface NoTimeSlotsMessageProps {
  showReplacementServiceButton: boolean;
  serviceId: string;
  replacementService?: ReplacementService;
  onButtonClick: () => void;
}

const StyledText = styled(Text)`
  &&& {
    text-align: center;
  }
`;

const StyledButton = styled(Button)`
  margin-top: 8px;
  display: block;
  margin-left: auto;
  margin-right: auto;
  margin-bottom: 16px;
`;

const StyledDivider = styled.div`
  margin: 1rem 0;
  border-top: 1px solid ${colors.neutral.grey200};
  width: 100%;
`;

const NeutralInlineMessage = styled(InlineMessage)`
  &&& {
    svg {
      fill: ${colors.icon.default};
    }
  }
`;

const RootContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const NoTimeSlotsMessage: React.FC<NoTimeSlotsMessageProps> = ({
  showReplacementServiceButton,
  serviceId,
  replacementService,
  onButtonClick,
}) => {
  const { t } = useTranslation();
  const replacementServiceStore = useStateSelector(
    (state) => state.app.selected.replacementService?.store,
  );

  if (showReplacementServiceButton) {
    const replacementServiceName = t(
      `services.name.${replacementService?.serviceId}`,
    );
    const storeName = getStoreName(
      t,
      replacementServiceStore?.text || '',
      replacementService?.location,
    );
    return (
      <RootContainer>
        <StyledText textStyle="Body.M">
          {t(
            `components.timePicker.noTimeslots.SELECT_A_REPLACEMENT_SERVICE_ERROR`,
          )}
        </StyledText>
        <StyledDivider />
        <StyledButton
          aria-label={`${replacementServiceName}, ${storeName}`}
          text={replacementServiceName}
          type="emphasised"
          onClick={onButtonClick}
          ssrIcon={Calendar}
        />
        <NeutralInlineMessage subtle body={storeName} ssrIcon={locationPin} />
      </RootContainer>
    );
  }
  return (
    <StyledText textStyle="Body.M">
      {t(`components.timePicker.noTimeslots.${serviceId}`)}
    </StyledText>
  );
};

export default NoTimeSlotsMessage;
